import { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { format, fromZonedTime } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import Icon from '../../../Icons'
import { getMinutesofDayFromDate, dayMinutesToHoursMinutes } from '../../../../helpers/helpers'
import { selectTimeRange } from '../helpers'
import API from '../../../../api/api'

function TimeSelectView({
  amenity,
  selectedDay,
  timezone,
  onBack,
  onReserveClick,
  setSelectedTimeScopes,
  displayHeader = true,
  isLoading,
  externalErrorMessage,
}) {
  const { t } = useTranslation(['client-reservations'])
  const [timeScopes, setTimeScopes] = useState([])
  const [selectedTime, setSelectedTime] = useState(undefined)
  const [slotsDisabled, setSlotsDisabled] = useState([])
  const [errorMessage, setErrorMessage] = useState(null)
  const [loadingTimeScopes, setLoadingTimeScopes] = useState(true)
  const timeout = useRef(null)

  const fetchData = async () => {
    try {
      const responseData = await API.getTimeScopes(
        amenity.id,
        fromZonedTime(selectedDay.setHours(0, 0, 0, 0), timezone).getTime(),
      )
      if (format(new Date(), 'yyyy-MM-dd') === selectedDay) {
        const actualTime = getMinutesofDayFromDate(new Date())
        const availableTimeScopes = responseData.filter((data) => data.startTime > actualTime)
        setTimeScopes(availableTimeScopes)
      } else {
        setTimeScopes(responseData)
      }
    } catch (err) {
      console.error(err)
    } finally {
      setLoadingTimeScopes(false)
    }
  }

  useEffect(() => {
    fetchData()
    return () => {
      if (timeout.current) clearTimeout(timeout.current)
    }
  }, [])

  useEffect(() => {
    const slotsDisabled = timeScopes
      .filter(({ reservations }) => reservations >= amenity.capacity)
      .map(({ startTime }) => startTime)

    setSlotsDisabled(slotsDisabled)
  }, [timeScopes])

  useEffect(() => {
    setSelectedTimeScopes(selectedTime)
  }, [selectedTime])

  useEffect(() => {
    setErrorMessage(externalErrorMessage)
  }, [externalErrorMessage])

  const onTimePick = ({ startTime, endTime }) => {
    const { timeInterval, minTime, maxTime, availableEndTime } = amenity

    // Handle single time slot selection with minimum time requirement
    if (
      !endTime &&
      timeInterval < minTime &&
      startTime + minTime - timeInterval < availableEndTime
    ) {
      return setSelectedTime({
        startTime,
        endTime: startTime + minTime - timeInterval,
      })
    }

    // Handle maximum time limit exceeded
    if (endTime && endTime + timeInterval - startTime > maxTime) {
      setSelectedTime(undefined)
      setErrorMessage(t('client-reservations:timeSelect.maximumTimeAllowed', { max: maxTime }))

      // Clear existing timeout before setting new one
      if (timeout.current) clearTimeout(timeout.current)
      timeout.current = setTimeout(() => setErrorMessage(null), 3000)
      return
    }

    // Check for continuous time slots when endTime is selected
    if (endTime) {
      const availableSlots = timeScopes.map((slot) => slot.startTime)
      let currentTime = startTime

      while (currentTime < endTime) {
        const nextSlot = currentTime + timeInterval
        if (!availableSlots.includes(nextSlot)) {
          setSelectedTime({ startTime: endTime, endTime: undefined })
          return
        }
        currentTime = nextSlot
      }
    }

    // Default case: pass through the selected time range
    setSelectedTime({ startTime, endTime })
  }

  const onClickTimeSlot = (startTime) => {
    const time =
      amenity.timeInterval < amenity.maxTime
        ? selectTimeRange({ timeSlot: startTime, selectedTime, slotsDisabled })
        : { startTime }

    onTimePick(time)
  }

  return (
    <div className="time-picker-container">
      {displayHeader ? (
        <div className="header">
          <div className="arrow-back" onClick={() => onBack()}>
            <Icon icon={'arrow'} />
          </div>
          <span>{t('client-reservations:timeSelect.selectTimeSlots')}</span>
        </div>
      ) : null}
      <div className="time-scopes">
        {loadingTimeScopes ? (
          <div className="loader" />
        ) : timeScopes.length ? (
          timeScopes.map(({ startTime }, i) => {
            const selected =
              (selectedTime && selectedTime.startTime === startTime) ||
              (selectedTime && selectedTime.endTime === startTime)
            const highlighted = !!(
              ((selectedTime && selectedTime.startTime) || 0) < startTime &&
              ((selectedTime && selectedTime.endTime) || 0) > startTime
            )
            const disabled = slotsDisabled.includes(startTime)

            return (
              <div
                key={i}
                onClick={() => onClickTimeSlot(startTime)}
                className={`time-scope-button ${
                  selected || highlighted
                    ? 'time-scope-button-highlighted'
                    : disabled
                      ? 'time-scope-button-disabled'
                      : ''
                }`}
              >
                {dayMinutesToHoursMinutes(startTime)}
              </div>
            )
          })
        ) : (
          <div className="no-time-scopes">
            {t('client-reservations:timeSelect.noTimeSlotsAvailable')}
          </div>
        )}
      </div>
      <div className="reservation-summary-footer">
        {errorMessage ? <div className="error-message">{errorMessage}</div> : null}
        {selectedTime ? (
          <div className="reservation-time-scopes">
            <div className="time-scope">
              {t('client-reservations:timeSelect.startTime')}{' '}
              <span className="bold">{dayMinutesToHoursMinutes(selectedTime.startTime)}</span>
            </div>
            <div className="time-scope">
              {t('client-reservations:timeSelect.endTime')}{' '}
              <span className="bold">
                {dayMinutesToHoursMinutes(
                  (!selectedTime.endTime ? selectedTime.startTime : selectedTime.endTime) +
                    amenity.timeInterval,
                )}
              </span>
            </div>
          </div>
        ) : null}
        <div className="reservation-button">
          <button
            disabled={!selectedTime}
            className={`button ${isLoading ? 'is-loading' : ''} button-primary`}
            onClick={() => onReserveClick()}
          >
            {t('client-reservations:actions.reserveNow')}
          </button>
        </div>
      </div>
    </div>
  )
}

TimeSelectView.propTypes = {
  amenity: PropTypes.object,
  selectedDay: PropTypes.instanceOf(Date),
  timezone: PropTypes.string,
  onBack: PropTypes.func,
  onReserveClick: PropTypes.func,
  setSelectedTimeScopes: PropTypes.func,
  displayHeader: PropTypes.bool,
  isLoading: PropTypes.bool,
  externalErrorMessage: PropTypes.string,
}

export default TimeSelectView
